import React, { useState, useEffect } from "react";
import { CSpinner } from "@coreui/react-pro";
import { useNavigate, useParams } from "react-router-dom";
import { ReactComponent as EyeIcon } from "../../assets/icons/eye.svg";
import { ReactComponent as EyeSlashIcon } from "../../assets/icons/eye-slash.svg";
import { getRoleNames, editUser, getUser } from "../../utils/backend";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import { useField } from "formik";
import { toast } from "react-toastify";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";

const EditUser = () => {
  const [isPasswordVisible, setIsPasswordVisible] = useState(false);
  const [roleNames, setRoleNames] = useState([]);
  const [isLoadingRoles, setIsLoadingRoles] = useState(false);
  const [isSubmittingForm, setIsSubmittingForm] = useState(false);
  const { id } = useParams();
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const [initialValues, setInitialValues] = useState({
    username: "",
    full_name: "",
    role: "",
    email: "",
    phone_number: "",
    country_code: "+60",
    id_number: "", // updated
  });
  const [originalValues, setOriginalValues] = useState({});

  useEffect(() => {
    fetchData(id);
  }, [id]);

  const fetchData = async (id) => {
    setIsLoading(true);
    try {
      const response = await getUser({ id: id });
      const fetchedValues = {
        salutation: response.data.salutation || "",
        username: response.data.username || "",
        full_name: response.data.full_name || "",
        email: response.data.email || "",
        phone_number: response.data.phone_number && response.data.phone_number.startsWith("+60")
          ? response.data.phone_number.substring(3)
          : response.data.phone_number || "",
        country_code: response.data.phone_number && response.data.phone_number.startsWith("+60") ? "+60" : "+60",
        vehicle_type: response.data.vehicle_type || "",
        ownership_type: response.data.ownership_type || "",
        vehicle_brand: response.data.vehicle_brand || "",
        vehicle_model: response.data.vehicle_model || "",
        vehicle_variants: response.data.vehicle_variants || "",
        vehicle_cc: response.data.vehicle_cc || "",
        vehicle_registration_date: response.data.vehicle_registration_date
          ? new Date(response.data.vehicle_registration_date)
              .toISOString()
              .split("T")[0]
          : "",
        id_type: (response.data.id_type || "").toUpperCase(),
        id_number: response.data.id_no || "",
        vehicle_registration_number:
          response.data.vehicle_registration_number || "",
        ownership_type: response.data.ownership_type || "",
        nationality: response.data.nationality || "",
        gender: response.data.gender || "",
        employment_status: response.data.employment_status || "",
        occupation: response.data.occupation || "",
        date_of_birth: response.data.date_of_birth
          ? new Date(response.data.date_of_birth).toISOString().split("T")[0]
          : "",
        address: response.data.address || "",
        postcode: response.data.postcode || "",
        city: response.data.city || "",
        state: response.data.state || "",
        marital_status: response.data.marital_status || "",
        contact_person_id_type: response.data.contact_person_id_type || "",
        contact_person_id_number: response.data.contact_person_id_number || "",
        contact_person_nationality:
          response.data.contact_person_nationality || "",
        business_name: response.data.business_name || "",
        business_registration_number:
          response.data.business_registration_number || "",
        business_contact_number: response.data.business_contact_number || "",
      };
      setInitialValues(fetchedValues);
      setOriginalValues({
        ...fetchedValues,
        // Store the original full phone number for comparison
        original_full_phone: response.data.phone_number || ""
      });
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const fetchRoleNames = async () => {
      setIsLoadingRoles(true);
      try {
        const response = await getRoleNames();
        setRoleNames(response.data);
      } catch (error) {
        console.error("Failed to fetch role names:", error);
      } finally {
        setIsLoadingRoles(false);
      }
    };

    fetchRoleNames();
  }, []);

  const validationSchema = Yup.object().shape({
    username: Yup.string()
      // .required("Username is required")
      .transform((value) => (value === "" ? undefined : value))
      .nullable()
      .trim()
      .min(3, "Username must be between 3 and 30 characters")
      .max(30, "Username must be between 3 and 30 characters")
      .matches(
        /^[a-zA-Z0-9_]+$/,
        "Username can only contain letters, numbers, and underscores"
      ),
    full_name: Yup.string()
      .required("Full name is required")
      .min(3, "Full Name must be between 3 and 50 characters")
      .max(50, "Full Name must be between 3 and 50 characters"),

    email: Yup.string()
      .email("Invalid email")
      .required("Email is required")
      .max(100, "Email must be less than 100 characters"),
    phone_number: Yup.string()
      .required("Phone number is required")
      .matches(
        /^\d{9,10}$/,
        "Phone Number must be between 9 and 10 digits."
      ),
    // id_number: Yup.string().required("ID Number is required"), // updated
    // id_type: Yup.string().required("ID Type is required"),
    salutation: Yup.string().required("Salutation is required"),

    // ownership_type: Yup.string()
    //     .required('Ownership Type is required'),
  });

  const togglePasswordVisibility = () => {
    setIsPasswordVisible(!isPasswordVisible);
  };

  const handleSubmit = async (
    values,
    { setSubmitting, setErrors, setFieldTouched }
  ) => {
    setIsSubmittingForm(true);
    try {
      const updatedValues = Object.keys(values).reduce((acc, key) => {
        if (key === "phone_number") {
          const currentFullPhoneNumber = values.country_code + values.phone_number;
          const originalFullPhoneNumber = originalValues.original_full_phone;
          if (currentFullPhoneNumber !== originalFullPhoneNumber) {
            acc[key] = currentFullPhoneNumber; 
          }
        } else if (key !== "country_code" && values[key] !== originalValues[key]) {
          acc[key] = values[key];
        }
        return acc;
      }, {});

      updatedValues.id = id;

      if (Object.keys(updatedValues).length > 1) {
        await editUser(updatedValues);
        toast.success("User updated successfully!");
        navigate(`/users/details/${updatedValues.id}`);
      } else {
        toast.info("No changes detected.");
        navigate(`/users/details/${updatedValues.id}`);
      }
    } catch (error) {
      console.error("Failed to edit user:", error);
      toast.error(error.message || "Failed to edit user. Please try again.");
      setErrors({ submit: "Failed to edit user", ...error.fieldErrors });
      Object.keys(values).forEach((field) =>
        setFieldTouched(field, true, false)
      );
    } finally {
      setSubmitting(false);
      setIsSubmittingForm(false);
    }
  };

  const handleBackClick = () => {
    const currentPath = window.location.pathname;
    if (currentPath.includes(`/details/${id}/edit`)) {
      navigate(`/users/details/${id}`);
    } else {
      navigate("/users");
    }
  };

  return (
    <>
      {/* <h5 className='mb-3'>Edit User</h5> */}
      {isLoading ? (
        <div className="card border-light">
          <div className="card-body p-4">
            <div className="row">
              <div className="col-md-6 mb-3">
                <Skeleton height={20} width={150} />
              </div>
              <div className="col-md-6 mb-3">
                <Skeleton height={20} width={150} />
              </div>
            </div>
            <div className="row">
              <div className="col-md-6 mb-3">
                <Skeleton height={20} width={150} />
              </div>
              <div className="col-md-6 mb-3">
                <Skeleton height={20} width={150} />
              </div>
            </div>
            <div className="row">
              <div className="col-md-6 mb-3">
                <Skeleton height={20} width={150} />
              </div>
              <div className="col-md-6 mb-3">
                <Skeleton height={20} width={150} />
              </div>
            </div>
            <div className="row">
              <div className="col-md-6 mb-3">
                <Skeleton height={20} width={150} />
              </div>
              <div className="col-md-6 mb-3">
                <Skeleton height={20} width={150} />
              </div>
            </div>
            <div className="row">
              <div className="col-md-6 mb-3">
                <Skeleton height={20} width={150} />
              </div>
              <div className="col-md-6 mb-3">
                <Skeleton height={20} width={150} />
              </div>
            </div>
            <div className="row">
              <div className="col-md-6 mb-3">
                <Skeleton height={20} width={150} />
              </div>
              <div className="col-md-6 mb-3">
                <Skeleton height={20} width={150} />
              </div>
            </div>
            <div className="d-flex justify-content-end">
              <Skeleton
                height={40}
                width={100}
                style={{ marginRight: "10px" }}
              />
              <Skeleton height={40} width={100} />
            </div>
          </div>
        </div>
      ) : (
        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
        >
          {({ isSubmitting, errors, touched, values }) => (
            <Form>
              {isSubmittingForm && (
                <div className="form-overlay">
                  <CSpinner color="primary" />
                </div>
              )}
              <div className="card border-light">
                <div className="card-body">
                  {errors.submit && (
                    <div className="alert alert-danger">{errors.submit}</div>
                  )}
                  <div className="row">
                  <div className="col-md-6 mb-3">
    <label htmlFor="salutation" className="form-label">
        <span className="text-danger">*</span> Salutation
    </label>
    <Field name="salutation">
        {({ field, form }) => (
            <>
             
                <select
                    {...field}
                    className={`form-control ${form.touched.salutation && form.errors.salutation ? 'is-invalid' : ''}`}
                >
                    <option value="">Select Salutation</option>
                    <option value="Ms">Ms</option>
                    <option value="Mr">Mr</option>
                    <option value="Mrs">Mrs</option>
                    {field.value !== '' &&field.value !== 'Ms' && field.value !== 'Mr' && field.value !== 'Mrs' && (
                        <option value={field.value}>{field.value}</option>
                    )}
                </select>
            </>
        )}
    </Field>
    <ErrorMessage name="salutation" component="div" className="invalid-feedback" />
</div>
                    <div className="col-md-6 mb-3">
                      <label htmlFor="username" className="form-label">
                        Username
                      </label>{" "}
                      <Field
                        name="username"
                        as="input"
                        type="text"
                        placeholder="Enter username"
                        className={`form-control ${
                          touched.username && errors.username
                            ? "is-invalid"
                            : ""
                        }`}
                      />
                      <ErrorMessage
                        name="username"
                        component="div"
                        className="invalid-feedback"
                      />
                    </div>
                    <div className="col-md-6 mb-3">
                      <label htmlFor="full_name" className="form-label">
                        <span className="text-danger">*</span> Full Name
                      </label>{" "}
                      <Field
                        name="full_name"
                        as="input"
                        type="text"
                        placeholder="Enter full name"
                        className={`form-control ${
                          touched.full_name && errors.full_name
                            ? "is-invalid"
                            : ""
                        }`}
                      />
                      <ErrorMessage
                        name="full_name"
                        component="div"
                        className="invalid-feedback"
                      />
                    </div>

                    <div className="col-md-6 mb-3">
                      <label htmlFor="email" className="form-label">
                        <span className="text-danger">*</span> Email
                      </label>{" "}
                      <Field
                        name="email"
                        as="input"
                        type="email"
                        placeholder="Enter email"
                        className={`form-control ${
                          touched.email && errors.email ? "is-invalid" : ""
                        }`}
                      />
                      <ErrorMessage
                        name="email"
                        component="div"
                        className="invalid-feedback"
                      />
                    </div>

                    <div className="col-md-6 mb-3">
    <label htmlFor="phone_number" className="form-label">
        <span className="text-danger">*</span> Phone Number
    </label>
    <div className="input-group">
        <Field as="select" name="country_code" className="form-select country-code-dropdown">
            <option value="+60">+60</option>
        </Field>
        <Field
            name="phone_number"
            as="input"
            type="tel"
            placeholder="xxxxxxxxx"
            className={`form-control ${
                touched.phone_number && errors.phone_number ? "is-invalid" : ""
            }`}
        />
            <ErrorMessage name="phone_number" component="div" className="invalid-feedback" />

    </div>
</div>

<style jsx>{`
    .country-code-dropdown {
        width: auto;
        max-width: 80px;
    }
`}</style>

                    <div className="col-md-6 mb-3">
                      <label htmlFor="id_type" className="form-label">
                         ID Type
                      </label>
                      <Field name="id_type">
                        {({ field, form }) => (
                          <>
                            <select
                              {...field}
                              className={`form-control ${
                                form.touched.id_type && form.errors.id_type
                                  ? "is-invalid"
                                  : ""
                              }`}
                              // disabled
                            >
                            
                              <option value="">Select ID Type</option>
                              <option value="NRIC">NRIC</option>
                              <option value="Passport">Passport</option>
                              {field.value !== '' &&field.value !== 'NRIC' && field.value !== 'Passport' && (
                                <option value={field.value}>{field.value}</option>
                              )}
                            </select>
                          </>
                        )}
                      </Field>
                      <ErrorMessage
                        name="id_type"
                        component="div"
                        className="invalid-feedback"
                      />
                    </div>

                    <div className="col-md-6 mb-3">
                      <label htmlFor="id_number" className="form-label">
                         ID Number
                      </label>{" "}
                      <Field
                        name="id_number"
                        as="input"
                        placeholder="Enter id number"
                        className={`form-control ${
                          touched.id_number && errors.id_number ? "is-invalid" : ""
                        }`}
                        // disabled
                      />
                      <ErrorMessage
                        name="id_number"
                        component="div"
                        className="invalid-feedback"
                      />
                    </div>
                    {/* 
                                        <div className="col-md-6 mb-3">
                                            <label htmlFor="vehicle_type" className="form-label">
                                                Vehicle Type
                                            </label>
                                            <Field name="vehicle_type" as="select" className={`form-control ${touched.vehicle_type && errors.vehicle_type ? 'is-invalid' : ''}`}>
                                                <option value="">Select Vehicle Type</option>
                                                <option value="Car">Car</option>
                                                <option value="Motorcycle">Motorcycle</option>
                                            </Field>
                                            <ErrorMessage name="vehicle_type" component="div" className="invalid-feedback" />
                                        </div>


                                        <div className="col-md-6 mb-3">
                                            <label htmlFor="vehicle_brand" className="form-label">
                                                Vehicle Brand
                                            </label>
                                            <Field name="vehicle_brand" as="input" type="text" placeholder="Enter vehicle brand" className={`form-control ${touched.vehicle_brand && errors.vehicle_brand ? 'is-invalid' : ''}`} />
                                            <ErrorMessage name="vehicle_brand" component="div" className="invalid-feedback" />
                                        </div>

                                        <div className="col-md-6 mb-3">
                                            <label htmlFor="vehicle_model" className="form-label">
                                                Vehicle Model
                                            </label>
                                            <Field name="vehicle_model" as="input" type="text" placeholder="Enter vehicle model" className={`form-control ${touched.vehicle_model && errors.vehicle_model ? 'is-invalid' : ''}`} />
                                            <ErrorMessage name="vehicle_model" component="div" className="invalid-feedback" />
                                        </div>

                                        <div className="col-md-6 mb-3">
                                            <label htmlFor="vehicle_variants" className="form-label">
                                                Vehicle Variants
                                            </label>
                                            <Field name="vehicle_variants" as="input" type="text" placeholder="Enter vehicle variants" className={`form-control ${touched.vehicle_variants && errors.vehicle_variants ? 'is-invalid' : ''}`} />
                                            <ErrorMessage name="vehicle_variants" component="div" className="invalid-feedback" />
                                        </div>


                                        <div className="col-md-6 mb-3">
                                            <label htmlFor="vehicle_cc" className="form-label">
                                                Vehicle CC
                                            </label>
                                            <Field name="vehicle_cc" as="input" type="text" placeholder="Enter vehicle cc" className={`form-control ${touched.vehicle_cc && errors.vehicle_cc ? 'is-invalid' : ''}`} />
                                            <ErrorMessage name="vehicle_cc" component="div" className="invalid-feedback" />
                                        </div>

                                        <div className="col-md-6 mb-3">
                                            <label htmlFor="vehicle_registration_date" className="form-label">
                                                Vehicle Registration Date
                                            </label>
                                            <Field name="vehicle_registration_date" as="input" type="date" className={`form-control ${touched.vehicle_registration_date && errors.vehicle_registration_date ? 'is-invalid' : ''}`} />
                                            <ErrorMessage name="vehicle_registration_date" component="div" className="invalid-feedback" />
                                        </div>

                                        <div className="col-md-6 mb-3">
                                            <span className="text-danger">*</span>  <label htmlFor="ownership_type" className="form-label">
                                                Ownership Type
                                            </label>
                                            <Field name="ownership_type" as="select" className={`form-control ${touched.ownership_type && errors.ownership_type ? 'is-invalid' : ''}`}>
                                                <option value="">Select Ownership Type</option>
                                                <option value="Company">Company</option>
                                                <option value="Private">Private</option>
                                            </Field>
                                            <ErrorMessage name="ownership_type" component="div" className="invalid-feedback" />
                                        </div> */}

                    {/* {values.ownership_type === 'Private' && (
                                            <>
                                                <div className="col-md-6 mb-3">
                                                    <label htmlFor="vehicle_registration_number" className="form-label">Vehicle Registration Number</label>
                                                    <Field name="vehicle_registration_number" as="input" type="text" className="form-control" />
                                                </div>
                                                <div className="col-md-6 mb-3">
                                                    <label htmlFor="id_type" className="form-label">ID Type</label>
                                                    <Field name="id_type" as="select" className="form-control">
                                                        <option value="">Select ID Type</option>
                                                        <option value="NRIC/MyKad">NRIC/MyKad</option>
                                                        <option value="Passport">Passport</option>
                                                        <option value="Army/Police/Other">Army/Police/Other</option>
                                                    </Field>
                                                </div>
                                                <div className="col-md-6 mb-3">
                                                    <label htmlFor="id_number" className="form-label">ID Number</label>
                                                    <Field name="id_number" as="input" type="text" className="form-control" />
                                                </div>
                                                <div className="col-md-6 mb-3">
                                                    <label htmlFor="nationality" className="form-label">Nationality</label>
                                                    <Field name="nationality" as="select" className="form-control">
                                                        <option value="">Select Nationality</option>
                                                        <option value="Malaysia">Malaysia</option>
                                                    </Field>
                                                </div>
                                                <div className="col-md-6 mb-3">
                                                    <label htmlFor="gender" className="form-label">Gender</label>
                                                    <Field name="gender" as="select" className="form-control">
                                                        <option value="">Select Gender</option>
                                                        <option value="Male">Male</option>
                                                        <option value="Female">Female</option>
                                                        <option value="Other">Other</option>
                                                    </Field>
                                                </div>
                                                <div className="col-md-6 mb-3">
                                                    <label htmlFor="employment_status" className="form-label">Employment Status</label>
                                                    <Field name="employment_status" as="select" className="form-control">
                                                        <option value="">Select Employment Status</option>
                                                        <option value="Full-time Employment">Full-time Employment</option>
                                                        <option value="Part-time Employment">Part-time Employment</option>
                                                        <option value="Contract">Contract</option>
                                                        <option value="Freelance">Freelance</option>
                                                        <option value="Self-employment">Self-employment</option>
                                                        <option value="Internship">Internship</option>

                                                    </Field>
                                                </div>
                                                <div className="col-md-6 mb-3">
                                                    <label htmlFor="occupation" className="form-label">Occupation</label>
                                                    <Field name="occupation" as="input" type="text" className="form-control" />
                                                </div>
                                                <div className="col-md-6 mb-3">
                                                    <label htmlFor="date_of_birth" className="form-label">Date of Birth</label>
                                                    <Field name="date_of_birth" as="input" type="date" className="form-control" />
                                                </div>
                                                <div className="col-md-6 mb-3">
                                                    <label htmlFor="address" className="form-label">Address</label>
                                                    <Field name="address" as="input" type="text" className="form-control" />
                                                </div>
                                                <div className="col-md-6 mb-3">
                                                    <label htmlFor="postcode" className="form-label">Postcode</label>
                                                    <Field name="postcode" as="input" type="text" className="form-control" />
                                                </div>
                                                <div className="col-md-6 mb-3">
                                                    <label htmlFor="city" className="form-label">City</label>
                                                    <Field name="city" as="input" type="text" className="form-control" />
                                                </div>
                                                <div className="col-md-6 mb-3">
                                                    <label htmlFor="state" className="form-label">State</label>
                                                    <Field name="state" as="input" type="text" className="form-control" />
                                                </div>
                                                <div className="col-md-6 mb-3">
                                                    <label htmlFor="marital_status" className="form-label">Marital Status</label>
                                                    <Field name="marital_status" as="select" className="form-control">
                                                        <option value="">Select Marital Status</option>
                                                        <option value="Single">Single</option>
                                                        <option value="Married">Married</option>
                                                    </Field>
                                                </div>
                                            </>
                                        )}
                                        {values.ownership_type === 'Company' && (
                                            <>
                                                <div className="col-md-6 mb-3">
                                                    <label htmlFor="contact_person_id_type" className="form-label">Company ID Type</label>
                                                    <Field name="contact_person_id_type" as="select" className="form-control">
                                                        <option value="">Select ID Type</option>
                                                        <option value="NRIC/MyKad">NRIC/MyKad</option>
                                                        <option value="Passport">Passport</option>
                                                        <option value="Army/Police/Other">Army/Police/Other</option>
                                                    </Field>
                                                </div>
                                                <div className="col-md-6 mb-3">
                                                    <label htmlFor="contact_person_id_number" className="form-label">Contact Person ID Number</label>
                                                    <Field name="contact_person_id_number" as="input" type="text" className="form-control" />
                                                </div>
                                                <div className="col-md-6 mb-3">
                                                    <label htmlFor="contact_person_nationality" className="form-label">Contact Person Nationality</label>
                                                    <Field name="contact_person_nationality" as="select" className="form-control">
                                                        <option value="">Select Nationality</option>
                                                        <option value="Malaysia">Malaysia</option>

                                                    </Field>
                                                </div>
                                                <div className="col-md-6 mb-3">
                                                    <label htmlFor="business_name" className="form-label">Business Name</label>
                                                    <Field name="business_name" as="input" type="text" className="form-control" />
                                                </div>
                                                <div className="col-md-6 mb-3">
                                                    <label htmlFor="business_registration_number" className="form-label">Business Registration Number</label>
                                                    <Field name="business_registration_number" as="input" type="text" className="form-control" />
                                                </div>
                                                <div className="col-md-6 mb-3">
                                                    <label htmlFor="business_contact_number" className="form-label">Business Contact Number</label>
                                                    <Field name="business_contact_number" as="input" type="text" className="form-control" />
                                                </div>
                                                <div className="col-md-6 mb-3">
                                                    <label htmlFor="address" className="form-label">Address</label>
                                                    <Field name="address" as="input" type="text" className="form-control" />
                                                </div>
                                                <div className="col-md-6 mb-3">
                                                    <label htmlFor="postcode" className="form-label">Postcode</label>
                                                    <Field name="postcode" as="input" type="text" className="form-control" />
                                                </div>
                                                <div className="col-md-6 mb-3">
                                                    <label htmlFor="city" className="form-label">City</label>
                                                    <Field name="city" as="input" type="text" className="form-control" />
                                                </div>
                                                <div className="col-md-6 mb-3">
                                                    <label htmlFor="state" className="form-label">State</label>
                                                    <Field name="state" as="input" type="text" className="form-control" />
                                                </div>
                                            </>
                                        )} */}
                  </div>
                  <div className=" d-flex justify-content-end">
                    <button
                      type="button"
                      className="btn me-3 ansa-button-secondary"
                      onClick={handleBackClick}
                    >
                      Back
                    </button>
                    <button
                      type="submit"
                      className="btn ansa-button-primary"
                      disabled={isSubmitting}
                    >
                      Save Changes
                    </button>
                  </div>
                </div>
              </div>
            </Form>
          )}
        </Formik>
      )}
    </>
  );
};

export default EditUser;
